import CabinSelectAWX from "@componentsAWX/pages/CabinSelect";
import CruiseAWX from "@componentsAWX/pages/Cruise";
import PassengersAWX from "@componentsAWX/pages/Passengers";
import PaymentAWXAirwallex from "@componentsAWX/pages/Payment/payment-providers/airwallex";
import PaymentAWX from "@componentsAWX/pages/Payment/payment-providers/authorize";
import PaymentConfirmationAWX from "@componentsAWX/pages/PaymentConfirmation";
import RoomsAWX from "@componentsAWX/pages/Rooms";
import SearchAWX from "@componentsAWX/pages/Search";
import SearchResultsAWX from "@componentsAWX/pages/SearchResults";
import CabinSelectCRT from "@componentsCRT/pages/CabinSelect";
import CruiseCRT from "@componentsCRT/pages/Cruise";
import PassengersCRT from "@componentsCRT/pages/Passengers";
import PaymentCRTAirwallex from "@componentsCRT/pages/Payment/payment-providers/airwallex";
import PaymentCRT from "@componentsCRT/pages/Payment/payment-providers/authorize";
import PaymentConfirmationCRT from "@componentsCRT/pages/PaymentConfirmation";
import RoomsCRT from "@componentsCRT/pages/Rooms";
import SearchCRT from "@componentsCRT/pages/Search";
import SearchResultsCRT from "@componentsCRT/pages/SearchResults";
import CabinSelectV1 from "@componentsV1/pages/CabinSelect";
import CruiseV1 from "@componentsV1/pages/Cruise";
import NickoTermsOfLiability from "@componentsV1/pages/NickoTermsOfLiability";
import PassengersV1 from "@componentsV1/pages/Passengers";
import PaymentV1Airwallex from "@componentsV1/pages/Payment/payment-providers/airwallex";
import PaymentV1 from "@componentsV1/pages/Payment/payment-providers/authorize";
import PaymentV1Stripe from "@componentsV1/pages/Payment/payment-providers/stripe";
import PaymentConfirmationV1 from "@componentsV1/pages/PaymentConfirmation";
import RoomsV1 from "@componentsV1/pages/Rooms";
import SearchV1 from "@componentsV1/pages/Search";
import SearchResultsV1 from "@componentsV1/pages/SearchResults";
import {
  RouteElement,
  ROOT,
  SEARCH_RESULTS,
  CRUISE,
  ROOMS,
  CABINS_SELECT,
  PASSENGERS,
  PAYMENT,
  PAYMENT_CONFIRMATION,
  UIVersion,
  PaymentProvider,
  LIABILITY_AND_CANCELLATION,
} from "@utils/constants/routes";
import i18n from "@utils/i18n";

const elementsByVersion = (
  paymentProvider: PaymentProvider,
): Record<UIVersion, Record<string, React.FC<React.PropsWithChildren>>> => ({
  v1: {
    search: SearchV1,
    searchResults: SearchResultsV1,
    cruise: CruiseV1,
    rooms: RoomsV1,
    cabinSelect: CabinSelectV1,
    passengers: PassengersV1,
    payment:
      paymentProvider === "authorize"
        ? PaymentV1
        : paymentProvider === "airwallex"
          ? PaymentV1Airwallex
          : paymentProvider === "stripe"
            ? PaymentV1Stripe
            : PaymentV1,
    paymentConfirmation: PaymentConfirmationV1,
    termsOfLiability: NickoTermsOfLiability,
  },
  crt: {
    search: SearchCRT,
    searchResults: SearchResultsCRT,
    cruise: CruiseCRT,
    rooms: RoomsCRT,
    cabinSelect: CabinSelectCRT,
    passengers: PassengersCRT,
    payment:
      paymentProvider === "authorize"
        ? PaymentCRT
        : paymentProvider === "airwallex"
          ? PaymentCRTAirwallex
          : PaymentCRT,
    paymentConfirmation: PaymentConfirmationCRT,
  },
  awx: {
    search: SearchAWX,
    searchResults: SearchResultsAWX,
    cruise: CruiseAWX,
    rooms: RoomsAWX,
    cabinSelect: CabinSelectAWX,
    passengers: PassengersAWX,
    payment:
      paymentProvider === "authorize"
        ? PaymentAWX
        : paymentProvider === "airwallex"
          ? PaymentAWXAirwallex
          : PaymentAWX,
    paymentConfirmation: PaymentConfirmationAWX,
  },
});

const privateRoutes = (uiVersion: UIVersion, paymentProvider: PaymentProvider): RouteElement => ({
  [ROOT]: {
    element: elementsByVersion(paymentProvider)[uiVersion || "v1"].search,
    layout: "auth",
    uiVersion: uiVersion || "v1",
    title: i18n.t("search"),
  },
  [SEARCH_RESULTS]: {
    element: elementsByVersion(paymentProvider)[uiVersion || "v1"].searchResults,
    layout: "search",
    title: i18n.t("search results"),
    uiVersion: uiVersion || "v1",
    crumbParams: ["start_date", "end_date"],
  },
  [CRUISE]: {
    element: elementsByVersion(paymentProvider)[uiVersion || "v1"].cruise,
    layout: "search",
    title: i18n.t("number of staterooms"),
    uiVersion: uiVersion || "v1",
    crumbParams: ["start_date", "end_date", "source"],
  },
  [ROOMS]: {
    element: elementsByVersion(paymentProvider)[uiVersion || "v1"].rooms,
    layout: "search",
    title: i18n.t("staterooms"),
    uiVersion: uiVersion || "v1",
    crumbParams: [
      "start_date",
      "end_date",
      "rooms",
      "room",
      "fare_1",
      "grade_1",
      "guests_1",
      "market_1",
      "source",
    ],
  },
  [CABINS_SELECT]: {
    element: elementsByVersion(paymentProvider)[uiVersion || "v1"].cabinSelect,
    layout: "search",
    title: i18n.t("staterooms"),
    uiVersion: uiVersion || "v1",
    crumbParams: [
      "start_date",
      "end_date",
      "rooms",
      "room",
      "fare_*",
      "grade_*",
      "guests_*",
      "market_*",
      "cabin_*",
      "deck_*",
      "source",
    ],
  },
  [PASSENGERS]: {
    element: elementsByVersion(paymentProvider)[uiVersion || "v1"].passengers,
    layout: "search",
    title: i18n.t("guests"),
    uiVersion: uiVersion || "v1",
    crumbParams: [
      "start_date",
      "end_date",
      "rooms",
      "room",
      "fare_*",
      "grade_*",
      "guests_*",
      "market_*",
      "cabin_*",
      "deck_*",
      "source",
    ],
  },
  [PAYMENT]: {
    element: elementsByVersion(paymentProvider)[uiVersion || "v1"].payment,
    layout: "search",
    uiVersion: uiVersion || "v1",
    title: i18n.t("payment"),
  },
  [PAYMENT_CONFIRMATION]: {
    element: elementsByVersion(paymentProvider)[uiVersion || "v1"].paymentConfirmation,
    layout: "search",
    uiVersion: uiVersion || "v1",
    title: i18n.t("payment_confirmation"),
  },
  [LIABILITY_AND_CANCELLATION]: {
    element: elementsByVersion(paymentProvider)[uiVersion || "v1"].termsOfLiability,
    layout: "search",
    uiVersion: uiVersion || "v1",
    title: i18n.t("liability_and_cancellation"),
  },
});

export default privateRoutes;
